import { DataView } from 'primereact/dataview';
import { useEffect, useState } from 'react';
import PageWrapper from '../../../../components/Display/PageWrapper';
import { useAccountContext } from '../../../../contexts/AccountContext';
import { useLanguageContext } from '../../../../contexts/LanguageContext';
import { useNavigationContext } from '../../../../contexts/NavigationContext';
import { privateFetch } from '../../../../utils/apiHelper';
import MultipleImagePanel from '../../common/image/MultipleImagePanel';
import DistributionListManager from '../distribution/DistributionListManager';
import DistributionManager from '../distribution/DistributionManager';
import NewsPanel from '../news/NewsPanel';
import ResourceDetail from './ResourceDetail';
import ResourceItem from './ResourceItem';
import ResourcePanel from './ResourcePanel';

function ResourcePage({ isActive }) {
  const { openResourceDetail, navigationContext, openProgramDetail } = useNavigationContext();
  const { accountContext } = useAccountContext();
  const [resourceList, setResourceList] = useState([]);
  const [resourceDetail, setResourceDetail] = useState(undefined);
  const [imageManagerIsOpen, setImagesManagerIsOpen] = useState(false);
  const [newsManagerIsOpen, setNewsManagerIsOpen] = useState(false);
  const [selectedNews, setSelectedNews] = useState(undefined);
  const [panelIsOpen, setPanelIsOpen] = useState(false);
  const [distributionListManagerIsOpen, setDistributionListManagerIsOpen] = useState(false);
  const [distributionManagerIsOpen, setDistributionManagerIsOpen] = useState(false);
  const [distribution, setDistribution] = useState(undefined);
  const [loading, setLoading] = useState(false);
  const { languageContext } = useLanguageContext();

  const fetchResourceList = async () => {
    await privateFetch(
      'GET',
      'banquetceleste',
      `/cu/${accountContext.id}/resource/?lang=${languageContext}`
    ).then((res) => {
      setResourceList(res);
    });
  };

  useEffect(() => {
    fetchResourceList();
  }, [languageContext]);

  const handleClickResource = (resource) => {
    openResourceDetail(resource);
  };

  const handleClickProgram = (program) => {
    openProgramDetail(program);
  };

  useEffect(() => {
    if (navigationContext.activeResourceId) {
      fetchResourceDetail();
    } else setResourceDetail(undefined);
  }, [navigationContext, languageContext]);

  const fetchResourceDetail = async () => {
    await privateFetch(
      'GET',
      'banquetceleste',
      `/cu/${accountContext.id}/resource/${navigationContext.activeResourceId}/?lang=${languageContext}`
    ).then((res) => {
      setResourceDetail(res);
      if (selectedNews) setSelectedNews(res.news.find((n) => n.id === selectedNews.id));
    });
  };

  const handleOpenManager = () => {
    setPanelIsOpen(true);
  };

  const createResource = async (resourceForm) => {
    setLoading(true);
    await privateFetch(
      'POST',
      'banquetceleste',
      `/cu/${accountContext.id}/resource/`,
      'La ressource a bien été ajouté',
      JSON.stringify(resourceForm)
    ).then(() => {
      fetchResourceList();
      setPanelIsOpen(false);
      setLoading(false);
    });
  };

  const updateResource = async (resourceForm) => {
    setLoading(true);
    await privateFetch(
      'PUT',
      'banquetceleste',
      `/cu/${accountContext.id}/resource/${resourceDetail.id}/?lang=${languageContext}`,
      'La ressource a bien été modifié',
      JSON.stringify(resourceForm)
    ).then(() => {
      fetchResourceList();
      setPanelIsOpen(false);
      fetchResourceDetail(resourceDetail.id);
      setLoading(false);
    });
  };

  const updateResourceImages = async (images) => {
    await privateFetch(
      'PUT',
      'banquetceleste',
      `/cu/${accountContext.id}/resource/${resourceDetail.id}/images/`,
      'Les images de la ressource ont bien été modifiées',
      JSON.stringify({
        images: images.map((image) => image.id)
      })
    ).then(() => {
      fetchResourceDetail();
      setImagesManagerIsOpen(false);
      setLoading(false);
    });
  };

  const createDistribution = async (distributionForm) => {
    await privateFetch(
      'POST',
      'banquetceleste',
      `/cu/${accountContext.id}/resource/${resourceDetail.id}/distribution/`,
      'La distribution a bien été ajouté',
      JSON.stringify(distributionForm)
    ).then(() => {
      fetchResourceDetail();
    });
  };

  const deleteDistribution = async (distribution) => {
    await privateFetch(
      'DELETE',
      'banquetceleste',
      `/cu/${accountContext.id}/distribution/${distribution.id}/`,
      'La distribution a bien été supprimé'
    ).then(() => {
      fetchResourceDetail();
    });
  };

  const handleDistributionOrderChange = async (distributions) => {
    const payload = distributions.map((d, index) => ({
      ...d,
      order: index
    }));
    await privateFetch(
      'PUT',
      'banquetceleste',
      `/cu/${accountContext.id}/resource/${resourceDetail.id}/distribution/`,
      "L'ordre de la distribution a bien été modifié",
      JSON.stringify(payload)
    ).then(() => {
      fetchResourceDetail();
      setDistributionManagerIsOpen(false);
    });
  };

  const updateDistribution = async (distributionForm) => {
    setLoading(true);
    await privateFetch(
      'PUT',
      'banquetceleste',
      `/cu/${accountContext.id}/distribution/${distribution.id}/?lang=${languageContext}`,
      'La ditribution a bien été modifiée',
      JSON.stringify(distributionForm)
    ).then(() => {
      fetchResourceList();
      setDistributionManagerIsOpen(false);
      fetchResourceDetail(resourceDetail.id);
      setLoading(false);
    });
  };

  const handleDeleteResource = async (resource) => {
    await privateFetch(
      'DELETE',
      'banquetceleste',
      `/cu/${accountContext.id}/resource/${resource.id}/`,
      'La ressource a bien été supprimé'
    ).then(() => {
      fetchResourceList();
      setResourceDetail(undefined);
      setLoading(false);
    });
  };

  const handleDeleteImage = async (image) => {
    await privateFetch(
      'DELETE',
      'resources',
      `/cu/${accountContext.id}/image/${image.id}/`,
      "L'image a bien été supprimée"
    ).then(() => {
      fetchResourceDetail();
    });
  };

  const handleCreateNews = () => {
    setSelectedNews(undefined);
    setNewsManagerIsOpen(true);
  };

  const handleUpdateNews = (news) => {
    setSelectedNews(news);
    setNewsManagerIsOpen(true);
  };

  const createProgramNews = async (newsForm) => {
    setLoading(true);
    await privateFetch(
      'POST',
      'banquetceleste',
      `/cu/${accountContext.id}/resource/${resourceDetail.id}/news/`,
      "L'article a bien été enregistré",
      JSON.stringify(newsForm)
    ).then(() => {
      fetchResourceDetail();
      setLoading(false);
      setNewsManagerIsOpen(false);
    });
  };

  const updateNews = async (newsForm) => {
    setLoading(true);
    await privateFetch(
      'PUT',
      'resources',
      `/cu/${accountContext.id}/news/${selectedNews.id}/`,
      "L'article a bien été modifiée",
      JSON.stringify(newsForm)
    ).then(() => {
      fetchResourceDetail();
      setLoading(false);
      setNewsManagerIsOpen(false);
    });
  };

  const handleDeleteNews = async (news) => {
    await privateFetch(
      'DELETE',
      'resources',
      `/cu/${accountContext.id}/news/${news.id}/`,
      "L'article a bien été supprimée"
    ).then(() => {
      fetchResourceDetail();
      setSelectedNews(undefined);
    });
  };

  const newResourceAction = {
    onClick: handleOpenManager,
    icon: 'pi-plus',
    label: 'Ajouter une ressource'
  };

  const goBackResourceAction = {
    onClick: handleClickResource,
    icon: 'pi-chevron-left',
    label: 'Retour à la liste'
  };

  const itemTemplate = (resource) => (
    <ResourceItem
      resource={resource}
      handleClickResource={() => handleClickResource(resource)}
      handleClickProgram={handleClickProgram}
    />
  );

  return (
    <div className={`h-full w-full flex-col ${isActive ? 'flex' : 'hidden'}`}>
      <PageWrapper
        scope="banquetceleste"
        label="Ressources"
        labelAction={handleClickResource}
        action={resourceDetail ? goBackResourceAction : newResourceAction}
        detail={resourceDetail}>
        {!resourceDetail ? (
          <DataView value={resourceList} itemTemplate={itemTemplate} />
        ) : (
          <ResourceDetail
            resource={resourceDetail}
            handleUpdateResource={handleOpenManager}
            handleUpdateDistributionList={() => setDistributionListManagerIsOpen(true)}
            handleUpdateDistribution={(distrib) => {
              setDistributionManagerIsOpen(true);
              setDistribution(distrib);
            }}
            handleDeleteDistribution={deleteDistribution}
            handleClickProgram={handleClickProgram}
            handleDelete={handleDeleteResource}
            handleUpdateImages={() => setImagesManagerIsOpen(true)}
            handleDeleteImage={handleDeleteImage}
            handleCreateNews={handleCreateNews}
            handleUpdateNews={handleUpdateNews}
            handleDeleteNews={handleDeleteNews}
          />
        )}
      </PageWrapper>
      <ResourcePanel
        isOpen={panelIsOpen}
        onClose={setPanelIsOpen}
        resource={resourceDetail}
        onCreate={createResource}
        onUpdate={updateResource}
        loading={loading}
      />
      <DistributionListManager
        isOpen={distributionListManagerIsOpen}
        onClose={setDistributionListManagerIsOpen}
        createDistribution={createDistribution}
        deleteDistribution={deleteDistribution}
        handleOrderChange={handleDistributionOrderChange}
        updateDistribution={updateDistribution}
        distributions={resourceDetail?.distributions}
        loading={loading}
      />

      <DistributionManager
        isOpen={distributionManagerIsOpen}
        onClose={setDistributionManagerIsOpen}
        updateDistribution={updateDistribution}
        distribution={distribution}
        loading={loading}
      />

      <MultipleImagePanel
        loading={loading}
        isOpen={imageManagerIsOpen}
        onClose={() => {
          setImagesManagerIsOpen(false);
        }}
        images={resourceDetail?.images}
        onValidate={updateResourceImages}
      />

      <NewsPanel
        news={selectedNews}
        loading={loading}
        isOpen={newsManagerIsOpen}
        onClose={() => {
          setSelectedNews(null);
          setNewsManagerIsOpen(false);
        }}
        onCreate={createProgramNews}
        onUpdate={updateNews}
      />
    </div>
  );
}

export default ResourcePage;
